import { CalendarIcon, Cross2Icon } from "@radix-ui/react-icons";
import { useEffect, useState } from "react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import {
    Popover,
    PopoverContent,
    PopoverTrigger,
} from "@/components/ui/popover";
import type { Matcher } from "react-day-picker";

interface SimpleDatePickerProps {
    value: Date | null;
    onChange: (date: Date | null) => void;
    placeholder?: string;
    className?: string;
    disabled?: boolean;
    fromDate?: Date;
    toDate?: Date;
    disabledDays?: Date[];
    showClearButton?: boolean;
}

function formatDate(date: Date): string {
    return date.toLocaleDateString("en-US", {
        year: "numeric",
        month: "long",
        day: "numeric",
    });
}

export default function DatePicker({
    value,
    onChange,
    placeholder = "Select date",
    className,
    disabled = false,
    fromDate,
    toDate,
    disabledDays,
    showClearButton = true,
}: SimpleDatePickerProps) {
    const [calendarOpen, setCalendarOpen] = useState(false);

    const [timeZone, setTimeZone] = useState<string | undefined>(undefined);

    useEffect(() => {
        setTimeZone(Intl.DateTimeFormat().resolvedOptions().timeZone);
    }, []);

    const isValidDate = value instanceof Date && !isNaN(value.getTime());

    const handleDateSelect = (date: Date | undefined) => {
        onChange(date || null);
        setCalendarOpen(false);
    };

    const handleClear = (e: React.MouseEvent) => {
        e.stopPropagation();
        onChange(null);
    };

    // Build the disabled matcher for the calendar
    const disabledMatcher: Matcher | Matcher[] | undefined = (() => {
        const matchers: Matcher[] = [];

        if (fromDate) {
            matchers.push({ before: fromDate });
        }

        if (toDate) {
            matchers.push({ after: toDate });
        }

        if (disabledDays && disabledDays.length > 0) {
            matchers.push(...disabledDays);
        }

        return matchers.length > 0 ? matchers : undefined;
    })();

    return (
        <Popover open={calendarOpen} onOpenChange={setCalendarOpen}>
            <div className="flex gap-2">
                <PopoverTrigger asChild>
                    <Button
                        variant="outline"
                        disabled={disabled}
                        className={cn(
                            "w-full justify-start text-left font-normal",
                            !isValidDate && "text-muted-foreground",
                            className,
                        )}
                    >
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {isValidDate ? (
                            formatDate(value)
                        ) : (
                            <span>{placeholder}</span>
                        )}
                    </Button>
                </PopoverTrigger>
                {showClearButton && isValidDate && (
                    <Button
                        onClick={handleClear}
                        variant="outline"
                        size="icon"
                        disabled={disabled}
                    >
                        <Cross2Icon className="h-4 w-4" />
                    </Button>
                )}
            </div>
            <PopoverContent className="w-auto p-0" align="start">
                <Calendar
                    mode="single"
                    selected={isValidDate ? value : undefined}
                    onSelect={handleDateSelect}
                    disabled={disabledMatcher}
                    timeZone={timeZone}
                    autoFocus
                />
            </PopoverContent>
        </Popover>
    );
}
